import React from "react";
import ReactDOM from "react-dom/client";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from "react-router";

import * as Sentry from "@sentry/react";
import { MsalProvider } from "@azure/msal-react";
import "@fontsource-variable/mulish";

import App from "./App";
import { IS_PROD } from "./constants/Dev.constant";
import "./index.css";
import { initializeMsal } from "./msal";
import {
  hasForgotSpan,
  shouldDropForgotTransaction
} from "./utils/sentry.util";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const defaultTracesSampleRate = IS_PROD ? 0.1 : 1.0;
const authTracesSampleRate = IS_PROD ? 0.02 : 0.1;
const replaySessionSampleRate = IS_PROD ? 0.02 : 0.1;
const replayOnErrorSampleRate = IS_PROD ? 0.1 : 1.0;

// Initialize Sentry
Sentry.init({
  environment: import.meta.env.VITE_ENVIRONMENT || "localhost",
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true
    })
  ],
  tracesSampleRate: defaultTracesSampleRate,
  tracesSampler: (samplingContext) => {
    const { name, inheritOrSampleWith } = samplingContext;
    if (shouldDropForgotTransaction(name)) return 0;

    if (name.includes("/auth/")) {
      return inheritOrSampleWith(authTracesSampleRate);
    }

    return inheritOrSampleWith(defaultTracesSampleRate);
  },
  beforeSendTransaction: (event) => {
    if (
      shouldDropForgotTransaction(event.transaction) ||
      hasForgotSpan(event)
    ) {
      return null;
    }

    return event;
  },
  replaysSessionSampleRate: replaySessionSampleRate,
  replaysOnErrorSampleRate: replayOnErrorSampleRate,
  release: import.meta.env.VITE_APP_VERSION
    ? `fido-portal@${import.meta.env.VITE_APP_VERSION}`
    : undefined
});

// Initialize MSAL (kept as top-level await to preserve current startup behavior)
const msalInstance = await initializeMsal();

root.render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <App />
    </MsalProvider>
  </React.StrictMode>
);
